import React, { useState, useEffect } from "react";
import {
    Box, Button, Dialog, DialogTitle, DialogContent, TextField,
    Stack, Typography, Card, CardContent, Chip, Grid,
    CircularProgress, LinearProgress, List, ListItem, ListItemText, ListItemSecondaryAction,
    IconButton, Menu, MenuItem
} from "@mui/material";
import { VideoCall, WhatsApp, CheckCircle, MoreVert, EventBusy, EditCalendar, HourglassEmpty } from "@mui/icons-material";
import { collection, addDoc, query, where, onSnapshot, updateDoc, doc, getDocs, getDoc, orderBy, getCountFromServer } from "firebase/firestore";
import { db, auth } from "../firebase";
import { JitsiMeeting } from '@jitsi/react-sdk';
import Swal from "sweetalert2";
import moment from "moment";

function Videmeetings() {
    const [meetings, setMeetings] = useState([]);
    const [open, setOpen] = useState(false);
    const [sendingOpen, setSendingOpen] = useState(false);
    const [isAutoSending, setIsAutoSending] = useState(false);
    const [activeMeeting, setActiveMeeting] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [parentList, setParentList] = useState([]);
    const [sentCount, setSentCount] = useState(0);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMeeting, setSelectedMeeting] = useState(null);

    const [formData, setFormData] = useState({
        type: "Parent Meeting",
        date: "",
        description: "",
    });

    // --- AUTO SEND LOGIC ---
    useEffect(() => {
        const processQueue = async () => {
            if (sendingOpen && isAutoSending && sentCount < parentList.length) {
                const currentIndex = sentCount;
                const parent = parentList[currentIndex];

                // Trigger the API call
                const success = await sendSingleWhatsApp(parent.mobile);

                // Update local state to show checkmark
                const newList = [...parentList];
                newList[currentIndex].sent = true;
                setParentList(newList);

                // Increment count to trigger next iteration
                setSentCount(prev => prev + 1);
            } else if (sentCount > 0 && sentCount === parentList.length) {
                setIsAutoSending(false);
                Swal.fire("Broadcast Complete", `Successfully notified ${sentCount} parents.`, "success");
            }
        };

        processQueue();
    }, [sentCount, sendingOpen, isAutoSending]);

    useEffect(() => {
        const fetchAdminData = async () => {
            if (auth.currentUser) {
                const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
                if (userSnap.exists()) {
                    const data = userSnap.data();
                    setUserData(data);
                    fetchMeetings(data.schoolId);
                }
            }
            setLoading(false);
        };
        fetchAdminData();
    }, []);

    const fetchMeetings = (sId) => {
        const q = query(collection(db, "meetings"), where("schoolId", "==", sId), orderBy("createdAt", "desc"));
        onSnapshot(q, (snapshot) => {
            setMeetings(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    };

    const handleCreateAndNotify = async () => {
        if (!formData.description || !formData.date) return Swal.fire("Error", "Fill details", "error");

        try {
            const formattedDate = moment(formData.date).format("DD/MM/YY hh:mm A");
            const roomName = `MMS_PTM_${Math.random().toString(36).substring(7)}`;

            await addDoc(collection(db, "meetings"), {
                ...formData,
                date: formattedDate,
                schoolId: userData.schoolId,
                status: "Upcoming",
                createdAt: new Date(),
                roomName: roomName
            });

            const q = query(collection(db, "users"), where("schoolId", "==", userData.schoolId), where("role", "==", "Parent"));
            const parentSnaps = await getDocs(q);
            const list = parentSnaps.docs.map(d => ({ id: d.id, ...d.data(), sent: false }));

            if (list.length === 0) {
                setOpen(false);
                return Swal.fire("Success", "Meeting scheduled, but no parents found to notify.", "info");
            }

            setParentList(list);
            setSentCount(0);
            setOpen(false);
            setSendingOpen(true);
            setIsAutoSending(true); // Start the auto-send loop
        } catch (e) {
            Swal.fire("Error", "Failed to schedule", "error");
        }
    };

    const sendSingleWhatsApp = async (mobile) => {
        try {
            const walletDoc = await getDoc(doc(db, "wallets", "0YVnAJmiq8cajkf4ufE5a8xYoFQ2"));
            const totalMessages = walletDoc.exists() ? walletDoc.data().totalMessages : 0;
            const historySnapshot = await getCountFromServer(collection(db, "whatsapphistory"));
            const remaining = totalMessages - historySnapshot.data().count;

            if (remaining <= 0) return false;

            const whatsappSnap = await getDocs(collection(db, "whatsapp"));
            const whatsappData = whatsappSnap.docs[0].data();

            const payload = {
                messaging_product: "whatsapp",
                to: `91${mobile}`,
                type: "template",
                template: {
                    name: "shipment_detai",
                    language: { code: "en" },
                    components: [{
                        type: "body",
                        parameters: [
                            { type: "text", text: moment(formData.date).format("DD/MM/YY hh:mm A") },
                            { type: "text", text: formData.description }
                        ],
                    }],
                },
            };

            const response = await fetch(`https://graph.facebook.com/v22.0/${whatsappData.phoneid}/messages`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${whatsappData.accesstoken}` },
                body: JSON.stringify(payload),
            });

            const res = await response.json();
            const status = res.messages?.[0]?.id ? "sent" : "failed";

            await addDoc(collection(db, "whatsapphistory"), {
                mobile, postTitle: "Parent Meeting Notification", type: "template", status, timestamp: new Date().toISOString(),
            });

            await new Promise(r => setTimeout(r, 800)); // Small delay to prevent API rate limits
            return status === "sent";
        } catch (err) { return false; }
    };

    // Reschedule/Cancel handlers (Keeping your existing logic)
    const handleCancelMeeting = async (id) => {
        const result = await Swal.fire({ title: "Cancel Meeting?", icon: "warning", showCancelButton: true });
        if (result.isConfirmed) {
            await updateDoc(doc(db, "meetings", id), { status: "Cancelled" });
            setAnchorEl(null);
        }
    };

    const handleReschedule = async (m) => {
        const { value: newDate } = await Swal.fire({
            title: "New Date/Time",
            html: '<input type="datetime-local" id="swal-input1" class="swal2-input">',
            preConfirm: () => document.getElementById('swal-input1').value
        });
        if (newDate) {
            await updateDoc(doc(db, "meetings", m.id), { date: moment(newDate).format("DD/MM/YY hh:mm A"), status: "Rescheduled" });
            setAnchorEl(null);
        }
    };

    const handleStartMeeting = async (m) => {
        try {
            // If it's not already started, update Firestore
            if (m.status !== "Started") {
                await updateDoc(doc(db, "meetings", m.id), { status: "Started" });
            }
            setActiveMeeting(m);
        } catch (error) {
            Swal.fire("Error", "Could not update meeting status", "error");
        }
    };

    const endMeeting = async () => {
        if (activeMeeting && activeMeeting.id) {
            try {
                await updateDoc(doc(db, "meetings", activeMeeting.id), {
                    status: "Completed"
                });
                setActiveMeeting(null); // This closes the dialog
            } catch (error) {
                console.error("Error ending meeting:", error);
                // Close dialog anyway so the user isn't stuck
                setActiveMeeting(null);
            }
        } else {
            setActiveMeeting(null);
        }
    };



    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" mb={4}>
                <Typography variant="h5" fontWeight="900" color="#b71c1c">Parent Meeting Portal</Typography>
                <Button variant="contained" onClick={() => setOpen(true)} sx={{ bgcolor: "#b71c1c" }}>Schedule New</Button>
            </Stack>

            <Grid className="row" spacing={3}>
                {meetings.map((m) => (
                    <Grid className="col-sm-4 mb-3" item xs={12} sm={6} md={4} key={m.id}>
                        <Card sx={{ borderRadius: 4, borderTop: `5px solid ${m.status === 'Cancelled' ? 'grey' : '#b71c1c'}`, opacity: m.status === 'Cancelled' ? 0.6 : 1 }}>
                            <CardContent>
                                <Stack direction="row" justifyContent="space-between">
                                    <Chip label={m.status} size="small" color={m.status === "Started" ? "success" : m.status === "Cancelled" ? "error" : "primary"} sx={{ mb: 1 }} />
                                    <IconButton size="small" onClick={(e) => { setAnchorEl(e.currentTarget); setSelectedMeeting(m); }}><MoreVert /></IconButton>
                                </Stack>
                                <Typography variant="h6" fontWeight="800">{m.description}</Typography>
                                <Typography variant="body2" color="textSecondary" mb={2}>{m.date}</Typography>
                                {/* Only show the button if the status is NOT Cancelled AND NOT Completed */}
                                {m.status !== "Cancelled" && m.status !== "Completed" && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<VideoCall />}
                                        onClick={() => handleStartMeeting(m)}
                                    >
                                        {m.status === "Started" ? "Join Now" : "Start Meeting"}
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* BROADCAST DIALOG - AUTOMATED */}
            <Dialog open={sendingOpen} fullWidth maxWidth="sm">
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    <WhatsApp sx={{ color: '#25D366', mr: 1, verticalAlign: 'middle' }} />
                    Automated Broadcast in Progress
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, mb: 3 }}>
                        <Grid container spacing={2} textAlign="center">
                            <Grid item xs={4}>
                                <Typography variant="caption" color="textSecondary">COMPLETED</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">{sentCount}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="textSecondary">REMAINING</Typography>
                                <Typography variant="h5" fontWeight="bold" color="warning.main">{parentList.length - sentCount}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="caption" color="textSecondary">TOTAL</Typography>
                                <Typography variant="h5" fontWeight="bold">{parentList.length}</Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Typography variant="body2" gutterBottom>
                        {sentCount < parentList.length ? `Sending notification to: ${parentList[sentCount]?.name || 'Parent'}...` : 'All notifications sent!'}
                    </Typography>

                    <LinearProgress
                        variant="determinate"
                        value={(sentCount / parentList.length) * 100}
                        sx={{ height: 12, borderRadius: 5, mb: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: '#25D366' } }}
                    />

                    <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee', borderRadius: 2 }}>
                        {parentList.map((parent, index) => (
                            <ListItem key={parent.id} size="small">
                                <ListItemText primary={parent.name} secondary={parent.mobile} />
                                <ListItemSecondaryAction>
                                    {parent.sent ? <CheckCircle sx={{ color: '#25D366' }} /> : <HourglassEmpty sx={{ color: '#ccc' }} />}
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>

                    <Button fullWidth variant="contained" color="inherit" onClick={() => setSendingOpen(false)} sx={{ mt: 2 }} disabled={isAutoSending}>
                        {isAutoSending ? "Please wait..." : "Close Dialog"}
                    </Button>
                </DialogContent>
            </Dialog>

            {/* CREATE MEETING DIALOG */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 900 }}>Schedule Meeting</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} mt={2}>
                        <TextField label="Meeting Date-Time" type="datetime-local" InputLabelProps={{ shrink: true }} fullWidth
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
                        <TextField label="Agenda/Details" multiline rows={3} fullWidth
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                        <Button variant="contained" fullWidth onClick={handleCreateAndNotify} sx={{ bgcolor: "#b71c1c", py: 1.5 }}>
                            Schedule & Broadcast
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* MENU & JITSI DIALOGS (Existing Logic) */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => handleReschedule(selectedMeeting)}>Reschedule</MenuItem>
                <MenuItem onClick={() => handleCancelMeeting(selectedMeeting.id)} sx={{ color: 'error.main' }}>Cancel Meeting</MenuItem>
            </Menu>

            <Dialog fullScreen open={Boolean(activeMeeting)} onClose={() => setActiveMeeting(null)}>
                <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    <Button
                        color="error"
                        variant="contained"
                        sx={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            zIndex: 1000,
                            boxShadow: 3
                        }}
                        onClick={endMeeting}
                    >
                        End & Leave Room
                    </Button>

                    {/* Only render Jitsi if activeMeeting and roomName exist */}
                    {activeMeeting?.roomName ? (
                        <JitsiMeeting
                            domain="meet.jit.si"
                            roomName={activeMeeting.roomName}
                            configOverwrite={{
                                startWithAudioMuted: true,
                                disableModeratorIndicator: false,
                                startScreenSharing: true,
                                enableEmailInStats: false
                            }}
                            interfaceConfigOverwrite={{
                                DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
                                SHOW_JITSI_WATERMARK: false
                            }}
                            userInfo={{
                                displayName: userData?.name || "School Admin"
                            }}
                            onApiReady={(externalApi) => {
                                // Optional: Add event listeners here
                            }}
                            getIFrameRef={(iframeRef) => {
                                iframeRef.style.height = '100%';
                                iframeRef.style.width = '100%';
                            }}
                        />
                    ) : (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    )}
                </Box>
            </Dialog>
        </Box>
    );
}

export default Videmeetings;